import { AxiosResponse } from "axios";
import axiosInstance from "../../../middlewares/axiosInstance";
import {
  ExpiryReportRequest,
  ExpiryReportResponse,
  APIResponse,
  ExpiryReportFilters,
  ExpiryConfiguration,
} from "../types/expiry-report.types";

class ExpiryReportService {
  private baseUrl = "/business-admin/expiry-report";

  /**
   * Get expiry report data with filtering and pagination
   */
  async getExpiryReport(
    request: ExpiryReportRequest = {}
  ): Promise<AxiosResponse<APIResponse<ExpiryReportResponse>>> {
    const requestBody = {
      page: request.page || 1,
      page_size: request.page_size || 20,
      filters: request.filters || {},
      sort_field: request.sort_field || "days_until_expiry",
      sort_order: request.sort_order || "asc",
    };

    return axiosInstance.post(`${this.baseUrl}/list`, requestBody);
  }

  /**
   * Get expiry report summary only
   */
  async getExpiryReportSummary(
    filters: ExpiryReportFilters = {}
  ): Promise<AxiosResponse<APIResponse<any>>> {
    return axiosInstance.post(`${this.baseUrl}/summary`, { filters });
  }

  /**
   * Get expired items only
   */
  async getExpiredItems(
    pageSize: number = 50
  ): Promise<AxiosResponse<APIResponse<ExpiryReportResponse>>> {
    return this.getExpiryReport({
      page: 1,
      page_size: pageSize,
      filters: {
        expiry_status: "expired",
      },
      sort_field: "days_until_expiry",
      sort_order: "asc",
    });
  }

  /**
   * Get items expiring soon (near expiry)
   */
  async getItemsExpiringSoon(
    threshold: number = 7,
    pageSize: number = 50
  ): Promise<AxiosResponse<APIResponse<ExpiryReportResponse>>> {
    return this.getExpiryReport({
      page: 1,
      page_size: pageSize,
      filters: {
        expiry_status: "near_expiry",
        near_expiry_threshold: threshold,
      },
      sort_field: "days_until_expiry",
      sort_order: "asc",
    });
  }

  /**
   * Get items expiring within specific date range
   */
  async getItemsExpiringInRange(
    startDate: Date,
    endDate: Date,
    pageSize: number = 100
  ): Promise<AxiosResponse<APIResponse<ExpiryReportResponse>>> {
    return this.getExpiryReport({
      page: 1,
      page_size: pageSize,
      filters: {
        expiry_date_range: {
          start_date: startDate.getTime(),
          end_date: endDate.getTime(),
        },
      },
      sort_field: "expiry_date",
      sort_order: "asc",
    });
  }

  /**
   * Get expiry report for specific warehouse
   */
  async getWarehouseExpiryReport(
    warehouseLocation: string,
    pageSize: number = 100
  ): Promise<AxiosResponse<APIResponse<ExpiryReportResponse>>> {
    return this.getExpiryReport({
      page: 1,
      page_size: pageSize,
      filters: {
        warehouse_location: warehouseLocation,
      },
      sort_field: "days_until_expiry",
      sort_order: "asc",
    });
  }

  /**
   * Get expiry report for specific product
   */
  async getProductExpiryReport(
    productId: string,
    pageSize: number = 100
  ): Promise<AxiosResponse<APIResponse<ExpiryReportResponse>>> {
    return this.getExpiryReport({
      page: 1,
      page_size: pageSize,
      filters: {
        product_id: productId,
      },
      sort_field: "expiry_date",
      sort_order: "asc",
    });
  }

  /**
   * Get expiry configuration settings
   */
  async getExpiryConfiguration(): Promise<
    AxiosResponse<APIResponse<ExpiryConfiguration>>
  > {
    return axiosInstance.get(`${this.baseUrl}/configuration`);
  }

  /**
   * Update expiry configuration settings
   */
  async updateExpiryConfiguration(
    config: Partial<ExpiryConfiguration>
  ): Promise<AxiosResponse<APIResponse<ExpiryConfiguration>>> {
    return axiosInstance.put(`${this.baseUrl}/configuration`, config);
  }

  /**
   * Get critical alerts (items expiring very soon)
   */
  async getCriticalExpiryAlerts(
    criticalThreshold: number = 3
  ): Promise<AxiosResponse<APIResponse<ExpiryReportResponse>>> {
    return this.getExpiryReport({
      page: 1,
      page_size: 50,
      filters: {
        days_until_expiry_range: {
          min_days: -365, // Include already expired
          max_days: criticalThreshold,
        },
      },
      sort_field: "days_until_expiry",
      sort_order: "asc",
    });
  }

  /**
   * Export expiry report data
   */
  async exportExpiryReport(
    format: "excel" | "csv",
    filters: ExpiryReportFilters = {}
  ): Promise<AxiosResponse<Blob>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/export`,
      {
        format,
        filters,
      },
      {
        responseType: "blob",
      }
    );

    // Extract filename from response headers
    const contentDisposition = response.headers["content-disposition"];
    let filename = `expiry-report-${
      new Date().toISOString().split("T")[0]
    }.${format}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response;
  }

  /**
   * Mark batch as handled (for expired items)
   */
  async markBatchAsHandled(
    batchId: string,
    action: "disposed" | "returned" | "donated" | "other",
    notes?: string
  ): Promise<AxiosResponse<APIResponse<any>>> {
    return axiosInstance.post(`${this.baseUrl}/mark-handled`, {
      batch_id: batchId,
      action,
      notes,
      handled_date: new Date().getTime(),
    });
  }

  /**
   * Get expiry trends (historical data)
   */
  async getExpiryTrends(
    periodDays: number = 30
  ): Promise<AxiosResponse<APIResponse<any>>> {
    return axiosInstance.get(`${this.baseUrl}/trends`, {
      params: { period_days: periodDays },
    });
  }
}

export const expiryReportService = new ExpiryReportService();
